/**
 * Karta danych klienta (`ccard.*`): dodatkowe pola kreatora „Nowy klient”, decyzja działu Handlowego (płatność, B2B),
 * wydruk karty i sekcja karty na stronie klienta. Etykiety enumów: `cardEnum.*` (packages/shared). Scalane w `extra.ts`.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const ccardPl = {
  wizard: {
    deliveryDifferent: 'Adres dostawy inny niż adres firmy',
    deliveryDifferentHint: 'Włącz, gdy towar ma trafiać pod inny adres niż siedziba.',
    deliverySame: 'Jak adres firmy',
    suggestion: 'Podpowiedź: {value}',
    useSuggestion: 'Użyj',
    contactsHint: 'Osoby z karty klienta: właściciel, księgowość, biuro, pracownik… Co najmniej jedna z e-mailem lub telefonem.',
    addContact: 'Dodaj osobę',
    removeContact: 'Usuń osobę {n}',
    contactN: 'Osoba {n}',
    role: 'Rola',
    name: 'Imię i nazwisko',
    email: 'E-mail',
    phone: 'Telefon',
    copyReceiver: 'Skopiuj z kontaktu „Odbierający dostawy”',
    routePlaceholder: 'Wybierz trasę',
    noRoutes: 'Brak aktywnych tras — trasę ustali Logistyka.',
    choose: 'Wybierz',
    placeholders: {
      shortName: 'np. BISTRO ZIELONE',
      deliveryStreet: 'ul. Magazynowa 5',
      deliveryPostalCode: '00-000',
      deliveryCity: 'Poznań',
      avizationHours: 'np. 6:00–7:00, telefonicznie dzień wcześniej',
      receiverPhone: '+48 600 100 200',
      remarks: 'Wszystko, co warto wiedzieć o kliencie, a nie mieści się w polach powyżej…',
      contactName: 'Imię i nazwisko',
      contactEmail: 'osoba@firma.pl',
      contactPhone: '+48 600 100 200',
    },
    hints: {
      shortName: 'Symbol w Subiekcie: maks. 20 znaków, bez polskich liter.',
      routeId: 'Trasa dostaw — ostatecznie potwierdza ją Logistyka.',
      receiverPhone: 'Numer osoby, która przyjmuje towar.',
      paymentMethod: 'Wnioskowany rodzaj — dział Handlowy zatwierdza ostateczny.',
    },
    errors: {
      contactsRequired: 'Dodaj co najmniej jedną osobę z e-mailem lub telefonem.',
      contactEmpty: 'Podaj imię i nazwisko, e-mail lub telefon.',
      tooLong: 'Tekst jest za długi.',
    },
  },
  decision: {
    approvedPaymentMethod: 'Rodzaj płatności zaakceptowanej',
    b2bDecision: 'Konto na platformie B2B',
    b2bHint: 'Integracji z B2B jeszcze nie ma — zapisujemy decyzję („do założenia”).',
    requestedMethod: 'Wnioskowano: {value}',
    required: 'Wymagane przy zatwierdzeniu.',
  },
  print: {
    button: 'Drukuj kartę klienta',
    title: 'Karta danych klienta',
    print: 'Drukuj',
    close: 'Zamknij',
    hint: '„Drukuj” otwiera okno drukowania — można też zapisać kartę jako PDF.',
    request: 'Wniosek',
    customer: 'Klient',
    status: 'Status',
    date: 'Data',
    generated: 'Wygenerowano z NEXOPS · {date}',
    field: 'Pole',
    value: 'Dane',
    sameAsCompany: 'jak adres firmy',
    awaiting: 'czeka na decyzję działu',
    b2b: 'Konto B2B: {value}',
    terms: '{days} dni · {method}',
    rows: {
      fullName: 'Pełna nazwa',
      symbol: 'Symbol (nazwa skrócona)',
      companyAddress: 'Adres firmy',
      deliveryAddress: 'Adres dostawy',
      nip: 'NIP',
      emails: 'E-maile kontaktowe',
      phones: 'Telefony kontaktowe',
      owner: 'Opiekun',
      paymentRequested: 'Termin i rodzaj płatności wnioskowanej',
      paymentApproved: 'Termin i rodzaj płatności zaakceptowanej',
      targetDocument: 'Dokument docelowy',
      route: 'Trasa',
      priority: 'Priorytet',
      avizationHours: 'Godziny awizacji',
      receiverPhone: 'Telefon odbierającego dostawy',
      remarks: 'Uwagi',
    },
  },
  customer: {
    title: 'Karta danych klienta',
    deliveryAddress: 'Adres dostawy',
    payment: 'Płatność',
    targetDocument: 'Dokument docelowy',
    route: 'Trasa',
    priority: 'Priorytet dostaw',
    avizationHours: 'Godziny awizacji',
    receiverPhone: 'Telefon odbierającego',
    remarks: 'Uwagi',
    b2b: 'Konto B2B',
    contacts: 'Osoby kontaktowe',
    noRole: 'Bez roli',
    primary: 'główny',
    empty: '—',
  },
  payload: {
    legacyContact: 'Kontakt (wniosek sprzed karty klienta)',
  },
};

export const ccardEn: Shape<typeof ccardPl> = {
  wizard: {
    deliveryDifferent: 'Delivery address differs from the company address',
    deliveryDifferentHint: 'Turn on when goods go to another address than the registered office.',
    deliverySame: 'Same as company address',
    suggestion: 'Suggestion: {value}',
    useSuggestion: 'Use',
    contactsHint: 'People from the customer card: owner, accounting, office, employee… At least one with an e-mail or phone.',
    addContact: 'Add person',
    removeContact: 'Remove person {n}',
    contactN: 'Person {n}',
    role: 'Role',
    name: 'Full name',
    email: 'E-mail',
    phone: 'Phone',
    copyReceiver: 'Copy from the “Delivery receiver” contact',
    routePlaceholder: 'Choose a route',
    noRoutes: 'No active routes — Logistics will set the route.',
    choose: 'Choose',
    placeholders: {
      shortName: 'e.g. GREEN BISTRO',
      deliveryStreet: '5 Warehouse St.',
      deliveryPostalCode: '00-000',
      deliveryCity: 'Poznań',
      avizationHours: 'e.g. 6:00–7:00, by phone the day before',
      receiverPhone: '+48 600 100 200',
      remarks: 'Anything worth knowing about the customer that does not fit the fields above…',
      contactName: 'Full name',
      contactEmail: 'person@company.com',
      contactPhone: '+48 600 100 200',
    },
    hints: {
      shortName: 'Subiekt symbol: max 20 characters, no Polish letters.',
      routeId: 'Delivery route — Logistics confirms it.',
      receiverPhone: 'Phone of the person who receives the goods.',
      paymentMethod: 'Requested method — the Sales department approves the final one.',
    },
    errors: {
      contactsRequired: 'Add at least one person with an e-mail or phone.',
      contactEmpty: 'Enter a name, e-mail or phone.',
      tooLong: 'The text is too long.',
    },
  },
  decision: {
    approvedPaymentMethod: 'Approved payment method',
    b2bDecision: 'B2B platform account',
    b2bHint: 'There is no B2B integration yet — the decision is recorded (“to create”).',
    requestedMethod: 'Requested: {value}',
    required: 'Required when approving.',
  },
  print: {
    button: 'Print customer card',
    title: 'Customer data card',
    print: 'Print',
    close: 'Close',
    hint: '“Print” opens the print dialog — you can also save the card as PDF.',
    request: 'Request',
    customer: 'Customer',
    status: 'Status',
    date: 'Date',
    generated: 'Generated from NEXOPS · {date}',
    field: 'Field',
    value: 'Data',
    sameAsCompany: 'same as company address',
    awaiting: 'awaiting the department decision',
    b2b: 'B2B account: {value}',
    terms: '{days} days · {method}',
    rows: {
      fullName: 'Full name',
      symbol: 'Symbol (short name)',
      companyAddress: 'Company address',
      deliveryAddress: 'Delivery address',
      nip: 'Tax ID (NIP)',
      emails: 'Contact e-mails',
      phones: 'Contact phones',
      owner: 'Account manager',
      paymentRequested: 'Requested payment term and method',
      paymentApproved: 'Approved payment term and method',
      targetDocument: 'Target document',
      route: 'Route',
      priority: 'Priority',
      avizationHours: 'Delivery notice hours',
      receiverPhone: 'Delivery receiver phone',
      remarks: 'Remarks',
    },
  },
  customer: {
    title: 'Customer data card',
    deliveryAddress: 'Delivery address',
    payment: 'Payment',
    targetDocument: 'Target document',
    route: 'Route',
    priority: 'Delivery priority',
    avizationHours: 'Delivery notice hours',
    receiverPhone: 'Receiver phone',
    remarks: 'Remarks',
    b2b: 'B2B account',
    contacts: 'Contact persons',
    noRole: 'No role',
    primary: 'primary',
    empty: '—',
  },
  payload: {
    legacyContact: 'Contact (request from before the customer card)',
  },
};
